import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentsIcon from "@mui/icons-material/Payments";
import PetsIcon from "@mui/icons-material/Pets";
import { formatCurrency } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import ListItemRectangle from '../../layouts/ListItemRectangle';
import { useNavigate } from 'react-router-dom';

function ViewOwnerInfo({ owner, onViewDebtDetail, onAddPayment, petNaming }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handlePetClick = (pet) => {
    navigate('/main/view-pet', { state: { id: pet.id, updateLastView: false } });
  };

  return (
    <>
      {/* Owner states Section */}
      {owner.balance !== null && owner.balance > 0 && (     
        <Box
          style={{
            marginBottom: '8px',
            marginTop: '16px',
            padding: '8px',
            background: '#fff',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            gap: '8px',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Typography variant="caption">
              {t('view.ownerDebt', { amount: formatCurrency(owner.balance) })}
            </Typography>
            <Box>
              <Tooltip title={t('common.viewDetail')}>
                <IconButton
                  size="small"
                  onClick={() => onViewDebtDetail?.(owner)}
                  sx={{ color: 'text.secondary', mr: 1 }}
                >
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('view.registerPayment')}>
                <IconButton
                  size="small"
                  onClick={() => onAddPayment?.(owner)}
                  sx={{ color: 'text.secondary' }}
                >
                  <PaymentsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {/* Pets Section */}
      <Box
        style={{
          marginBottom: '16px',
          padding: '8px',
          background: '#fff',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          minHeight: '110px',
        }}
      >
        {owner.pets.length ? (
          <>
            <Typography variant="caption" style={{ margin: '8px' }}>
              {petNaming}
            </Typography>
            <Box 
              style={{ 
                display: 'flex', 
                gap: '8px',
                overflowX: 'auto',
                paddingBottom: '8px',
                WebkitOverflowScrolling: 'touch',
                msOverflowStyle: '-ms-autohiding-scrollbar'
              }}
            >
              {owner.pets.map(pet => (
                <ListItemRectangle
                  key={pet.id}
                  id={pet.id}
                  name={pet.name}
                  thumb_url={pet.thumb_url}
                  onClick={() => handlePetClick(pet)}
                  fallbackIcon={<PetsIcon />}
                  secondary={pet.death === 1 ? t('edit.decease') : null}
                />
              ))}
            </Box>
          </>
        ) 
        : 
        (
          <Typography variant="caption" style={{ margin: '8px' }}>
            {t('view.ownerNoPetsMessage', { name: petNaming.toLowerCase() })}
          </Typography>
        )}
      </Box>

      {/* Info Section */}
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.name')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {owner.name}
        </Typography>
      </Box>

      {/* Text fields with optional visibility */}
      {owner.address && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.address')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.address}
          </Typography>
        </Box>
      )}
      
      {owner.region && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.region')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.region.friendly_name}
          </Typography>
        </Box>
      )}

      {owner.phone && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.phone')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.phone}
          </Typography>
        </Box>
      )}

      {owner.email && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.email')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.email}
          </Typography>
        </Box>
      )}

      {owner.regional_id && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.regionalId')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.regional_id}
          </Typography>
        </Box>
      )}

      {owner.fiscal_type && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.fiscalType')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.fiscal_type.name}
          </Typography>
        </Box>
      )}

      {owner.notes && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.notes')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {owner.notes}
          </Typography>
        </Box>
      )}
    </>
  );
}

export default ViewOwnerInfo; 